import UserTable from "../../UserTable";
import { SocialCoachSessionService, UsersService } from "@/services";
import * as React from "react";
import { printBasicShortDate, URLS } from "@/utils";
import { UpdateEmailModal } from "../UpdateEmailModal";
import { useState } from "react";
import * as DTO from "../../../../interfaces/dtos";
import { RequestPWModal } from "@/containers/Admin/RequestPWModal";
import { Button, Table, DropdownMenu } from "@/components/ui";
import { Pencil, Lock, HatGlasses, MoreHorizontal } from "lucide-react";

export const AssistantsList: React.FC = () => {
  const [emailModal, setEmailModal] = useState(false);
  const [pwModal, setPWModal] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState<DTO.CompleteAccount>();

  const getActions = (_: DTO.CompleteAccount) => {
    return [
      { key: "edit", content: "Edit email" },
      { key: "pw", content: "Generate Password Link" },
      { key: "impersonate", content: "Impersonate Assistant" },
    ];
  };

  const handleAction = (index: number, assistant: DTO.CompleteAccount): void => {
    const actions = getActions(assistant);
    const action = actions[index];
    setSelectedAssistant(assistant);
    switch (action.key) {
      case "edit":
        setEmailModal(true);
        break;
      case "pw":
        setPWModal(true);
        break;
      case "impersonate":
        impersonate(assistant);
        break;
    }
  };

  const impersonate = (assistant: DTO.CompleteAccount) => {
    SocialCoachSessionService.impersonate(assistant.user.email!!).then(() => {
      // Redirect to coach dashboard
      window.location.assign(URLS.coach.dashboard);
    });
  };

  return (
    <>
      <UserTable
        headers={[
          { title: "Info" },
          { title: "Status" },
          { title: "Created" },
          { title: "Last Login" },
          { title: "Coach" },
          { title: "Actions" },
        ]}
        queryFunction={query => {
          return UsersService.searchAssistants(
            {
              searchTerm: query.searchTerm,
            },
            query.page,
            query.size
          );
        }}
        renderRow={(assistant: DTO.CompleteAccount) => {
          const actions = getActions(assistant);
          return (
            <React.Fragment key={`assistant-row-${assistant.user?.userId}`}>
              {/* Column 1: Info */}
              <Table.Cell className="py-3">
                <div className="flex flex-col gap-1">
                  <div className="font-semibold text-sm text-slate-700 leading-tight">
                    {assistant.profile?.firstName} {assistant.profile?.lastName}
                  </div>
                  <div className="text-xs text-slate-500 leading-none">{assistant.user.email}</div>
                </div>
              </Table.Cell>

              {/* Column 2: Status */}
              <Table.Cell className="py-3 border-l border-slate-100">
                <div className="flex flex-col gap-1 text-[11px]">
                  <div>
                    {assistant.user.status === "ACTIVE" ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200/40">
                        <span className="size-1 rounded-full bg-emerald-500 shrink-0" />
                        {assistant.user.status}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-wide bg-slate-50 text-slate-600 border border-slate-200/40">
                        <span className="size-1 rounded-full bg-slate-400 shrink-0" />
                        {assistant.user.status || "Inactive"}
                      </span>
                    )}
                  </div>
                </div>
              </Table.Cell>
              <Table.Cell className="py-3 border-l border-slate-100">
                <div className="text-slate-400 mt-0.5">
                  Created:{" "}
                  <span className="font-semibold text-slate-600">
                    {printBasicShortDate(assistant.user.createdDate)}
                  </span>
                </div>
              </Table.Cell>
              <Table.Cell className="py-3 border-l border-slate-100">
                <div className="text-slate-400">
                  Last Login:{" "}
                  <span className="font-semibold text-slate-600">
                    {printBasicShortDate(assistant.user.latestLoginDate)}
                  </span>
                </div>
              </Table.Cell>

              {/* Column 3: Coach */}
              <Table.Cell className="py-3 border-l border-slate-100">
                <div className="flex flex-col gap-1">
                  <div className="text-xs font-bold text-slate-800 tracking-tight leading-none">
                    {assistant.coach?.companyName ? (
                      assistant.coach.companyName
                    ) : (
                      <span className="italic text-slate-300 font-medium">No Company Name</span>
                    )}
                  </div>
                  <div className="text-xs font-semibold text-slate-700 leading-tight">
                    <span className="text-[10px] font-mono font-medium text-slate-500 bg-slate-50 border border-slate-200/60 px-1 py-0.5 rounded-sm mr-1">
                      {assistant.coach?.id}
                    </span>
                    {assistant.coach?.contactEmail}
                  </div>
                </div>
              </Table.Cell>

              {/* Column 4: Actions */}
              <Table.Cell className="py-3 text-right border-l border-slate-100">
                <div className="flex flex-row items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 hover:bg-slate-100 p-0 cursor-pointer outline-none"
                    onClick={() => impersonate(assistant)}
                    title="Impersonate"
                  >
                    <HatGlasses className="size-4 shrink-0 text-muted-foreground" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenu.Trigger>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 hover:bg-slate-100 p-0 cursor-pointer outline-none"
                      >
                        <MoreHorizontal className="size-4 shrink-0 text-muted-foreground" />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="end" className="w-52 border border-slate-100 shadow-md z-5000">
                      {actions.map((act, idx) => {
                        const IconComponent = (() => {
                          switch (act.key) {
                            case "edit":
                              return Pencil;
                            case "pw":
                              return Lock;
                            case "impersonate":
                              return HatGlasses;
                            default:
                              return MoreHorizontal;
                          }
                        })();
                        return (
                          <DropdownMenu.Item
                            key={act.key}
                            onClick={() => handleAction(idx, assistant)}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold cursor-pointer"
                          >
                            <IconComponent className="size-3.5 shrink-0 text-muted-foreground" />
                            <span>{act.content}</span>
                          </DropdownMenu.Item>
                        );
                      })}
                    </DropdownMenu.Content>
                  </DropdownMenu>
                </div>
              </Table.Cell>
            </React.Fragment>
          );
        }}
      />
      <UpdateEmailModal
        open={emailModal}
        onClose={() => setEmailModal(false)}
        currentEmail={selectedAssistant?.user?.email}
        userId={selectedAssistant?.user?.userId}
      />
      <RequestPWModal
        open={pwModal}
        onClose={() => setPWModal(false)}
        targetEmail={selectedAssistant?.user?.email || ""}
      />
    </>
  );
};
